import { Box, Button, Typography, Icon, CardMedia, Toolbar, AppBar } from "@mui/material";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/auth";
import { SettingsProvider } from "@/features/settings/contexts/settings-context";
import { ciCustomColors } from "@/theme/colors";
import logo_stacked from "@/assets/ci-logo-stacked-nav.svg";
import Navigation from "../navigation";
import { NAVIGATION_HEIGHT } from "../navigation/consts";
import { Icon404 } from "./Ilustration_404";

const NotFound = () => {
  const { t } = useTranslation("coi-platform-translations");
  const { user } = useAuth();
  const navigate = useNavigate();

  const isLoggedIn = Boolean(user?.current_user_id);
  const redirectTo = isLoggedIn ? "/consent-solutions" : "/login";
  const buttonLabel = isLoggedIn ? t("not_found.go_to_dashboard_button") : t("not_found.go_to_login_button");

  const content = (
    <Box
      width="100%"
      minHeight={isLoggedIn ? `calc(100vh - ${NAVIGATION_HEIGHT})` : "100vh"}
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      padding="24px"
      bgcolor={ciCustomColors.blue[600]}
      data-testid="not-found-page"
    >
      <Box
        width="100%"
        maxWidth="720px"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        gap="24px"
        bgcolor="white"
        padding="48px 120px"
        border={`1px solid ${ciCustomColors.blue[50]}`}
        borderRadius="4px"
      >
        <Icon
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "262px",
            height: "166px",
          }}
        >
          <Icon404 />
        </Icon>
        <Box display="flex" flexDirection="column" gap="8px" maxWidth="480px">
          <Typography variant="h4" fontWeight="bold" data-testid="not-found-page__title">
            {t("not_found.title")}
          </Typography>
          <Typography data-testid="not-found-page__description">{t("not_found.description")}</Typography>
        </Box>

        <Button
          variant="text"
          size="large"
          sx={{ border: `1.5px solid ${ciCustomColors.blue[50]}`, borderRadius: "4px", padding: "8px 16px" }}
          onClick={() => navigate({ to: redirectTo })}
          data-testid="not-found-page__cta"
        >
          {buttonLabel}
        </Button>
      </Box>
    </Box>
  );

  if (!isLoggedIn) {
    return (
      <>
        <AppBar
          color="secondary"
          position="fixed"
          sx={{
            height: NAVIGATION_HEIGHT,
            display: "flex",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            borderBottom: `2px solid ${ciCustomColors.blue[50]}`,
            boxShadow: "none",
            px: "34px",
          }}
        >
          <Toolbar disableGutters>
            <CardMedia
              component="img"
              image={logo_stacked}
              alt="Cookie Information Logo"
              sx={{
                maxWidth: {
                  xs: "100px",
                  md: "140px",
                },
                objectFit: "contain",
              }}
            />
          </Toolbar>
        </AppBar>
        {content}
      </>
    );
  }

  return (
    <SettingsProvider>
      <Navigation variant="reduced">{content}</Navigation>
    </SettingsProvider>
  );
};

export { NotFound };
